export default function CloudedTitleSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Dealing with <strong>Clouded Titles</strong> and Missing Heirs
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            A "clouded title" means there's uncertainty about who legally owns the property. This happens when previous transfers weren't properly recorded, heirs can't be located, or family members disagree about ownership. <strong>We buy houses with clouded titles</strong> and resolve these complex chain of title issues.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Makes a Title "Cloudy"?</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              A <strong>clear title</strong> means there's an unbroken chain of ownership from the current owner back through history. A <strong>clouded title</strong> has gaps, disputes, or defects in that chain. Common causes include:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Unrecorded deeds:</strong> Someone inherited the property but never recorded the transfer with the county</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Missing heirs:</strong> One or more heirs can't be located to sign off on the sale</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Quitclaim deed issues:</strong> Previous transfers used quitclaim deeds that don't guarantee clear title</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Errors in public records:</strong> Misspelled names, wrong legal descriptions, or recording mistakes</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Divorce complications:</strong> Property wasn't properly transferred during divorce proceedings</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Forgery or fraud:</strong> Someone forged a signature on a previous deed</p>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed">
              Traditional buyers and mortgage lenders require <strong>clear, marketable title</strong>. If your title is clouded, they'll walk away. But as <strong>cash home buyers in El Paso</strong>, we can purchase properties with title defects and work through the resolution process ourselves.
            </p>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/025205b23d128bbb245414952de5f955.jpg"
              alt="Family tree showing missing heirs and clouded title issues"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            The Fractional Interest Problem
          </h3>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=three%20adult%20siblings%20arguing%20in%20living%20room%20of%20inherited%20house%20pointing%20at%20documents%20looking%20frustrated%20and%20disagreeing%20about%20property%20sale%20family%20conflict%20over%20real%20estate%20tense%20atmosphere&width=800&height=600&seq=titleissues005&orientation=landscape"
                alt="Siblings disagreeing about inherited property sale"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </div>
            <div>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                One of the most common title issues we see: <strong>three siblings inherit a house, but only two want to sell.</strong> Each sibling owns a fractional interest (one-third each). Without all three signatures, the property can't be sold with clear title.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                The holdout sibling might be:
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">Emotionally attached to the family home</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">Unrealistic about the property's value</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">Using the property as leverage in family disputes</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-xl text-emerald-600 flex-shrink-0"></i>
                  <p className="text-gray-700">Simply unreachable or unresponsive</p>
                </li>
              </ul>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Your options are limited: file a <strong>partition lawsuit</strong> (expensive and time-consuming), buy out the holdout sibling (if you have the cash), or sell your fractional interest at a steep discount.
              </p>
              <div className="bg-emerald-500 text-white p-6 rounded-lg">
                <h4 className="font-bold text-xl mb-3">Our Solution:</h4>
                <p className="leading-relaxed">
                  We can purchase fractional interests or work with all parties to structure a deal that satisfies everyone. We've successfully closed deals where siblings were estranged for years. Our experience with <strong>selling inherited property with liens</strong> and family disputes means we know how to navigate these sensitive situations.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
              <i className="ri-file-search-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Chain of Title Issues</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              When the ownership history has gaps or inconsistencies, it creates uncertainty about who truly owns the property. We work with title companies to trace the chain and resolve discrepancies.
            </p>
            <p className="text-sm text-emerald-600 font-semibold">
              We've resolved chain of title issues going back 40+ years
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
              <i className="ri-user-search-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Missing Heirs</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Can't locate a sibling, cousin, or other heir who needs to sign? We have resources to conduct heir searches and can structure deals that protect all parties when heirs can't be found.
            </p>
            <p className="text-sm text-emerald-600 font-semibold">
              We've located missing heirs in 7 different states
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
              <i className="ri-file-edit-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Quitclaim Deeds</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Quitclaim deeds transfer whatever interest the grantor has, but don't guarantee clear title. If your property was transferred via quitclaim deed, we can still buy it and clear the title issues.
            </p>
            <p className="text-sm text-emerald-600 font-semibold">
              We accept properties with quitclaim deed histories
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Partition Lawsuits: The Last Resort
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                When co-owners can't agree on selling, a <strong>partition lawsuit</strong> forces the sale through the court system. The court appoints a commissioner to sell the property at auction, and the proceeds are divided among the owners.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Sounds simple, but partition lawsuits are:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <p className="text-gray-700"><strong>Expensive:</strong> Attorney fees of $10,000-$25,000+</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <p className="text-gray-700"><strong>Slow:</strong> 12-24 months from filing to sale</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <p className="text-gray-700"><strong>Unpredictable:</strong> Auction sales often bring below-market prices</p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                  <p className="text-gray-700"><strong>Destructive:</strong> Permanently damages family relationships</p>
                </li>
              </ul>
            </div>
            <div>
              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-lg mb-6">
                <h4 className="font-bold text-gray-900 text-xl mb-3">Better Alternative:</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Before filing a partition lawsuit, call us. We can often structure a deal that satisfies all parties without court involvement. We've successfully negotiated with holdout heirs, purchased fractional interests, and found creative solutions that avoid litigation.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Even if you've already filed a partition lawsuit, we can still help. We buy properties in partition proceedings and can close quickly, saving you months of waiting and thousands in legal fees.
                </p>
              </div>
              <div className="bg-gray-900 text-white p-6 rounded-lg">
                <p className="text-lg font-semibold mb-2">Average Savings vs. Partition Lawsuit:</p>
                <p className="text-4xl font-bold text-emerald-400">$18,500</p>
                <p className="text-sm mt-2">In legal fees and lost time value</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}